/**
 * TestExclusivo - Página exclusiva para suscriptores
 * Link privado para probar funcionalidad de pagos
 * Soporta Stripe (USD) y MercadoPago (ARS)
 * Misma funcionalidad que /sorprendeme pero minimalista
 */

import { BRAND_SOURCE_KEY, BRAND_NAME, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { STRIPE_PUBLISHABLE_KEY, STRIPE_APPEARANCE } from "@/config/stripe";
import { StripeCheckoutForm } from "@/components/StripeCheckoutForm";
import { MercadoPagoCheckoutForm } from "@/components/MercadoPagoCheckoutForm";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { Loader2 } from "lucide-react";

// Stripe singleton
let _stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> | null {
  if (!STRIPE_PUBLISHABLE_KEY) return null;
  if (!_stripePromise) {
    _stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return _stripePromise;
}

// Montos mínimos para prueba
const TEST_AMOUNT_USD = 1; // $1 USD
const TEST_AMOUNT_ARS = 1400; // $1400 ARS

type PaymentMethod = "stripe" | "mercadopago";
type Step = "select" | "checkout" | "success" | "error" | "pending";

export default function TestExclusivo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<Step>("select");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [processing, setProcessing] = useState(false);

  // Stripe
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // MercadoPago
  const [preferenceId, setPreferenceId] = useState<string | null>(null);

  // Donation tracking
  const [donationId, setDonationId] = useState<string | null>(null);

  // Handle return from payment
  useEffect(() => {
    const status = searchParams.get("status");
    const redirectStatus = searchParams.get("redirect_status");
    const checkoutStatus = searchParams.get("checkout");

    if (status === "success" || redirectStatus === "succeeded" || checkoutStatus === "success") {
      setStep("success");
      setSearchParams({});
    } else if (status === "error" || redirectStatus === "failed" || checkoutStatus === "failure") {
      setStep("error");
      setTimeout(() => {
        setSearchParams({});
        setStep("select");
      }, 3000);
    } else if (status === "pending" || checkoutStatus === "pending") {
      setStep("pending");
      setTimeout(() => {
        setSearchParams({});
        setStep("success");
      }, 3000);
    }
  }, [searchParams, setSearchParams]);

  const handleSelectPayment = async (method: PaymentMethod) => {
    setPaymentMethod(method);
    setProcessing(true);

    try {
      // 1. Guardar donación en la base de datos (igual que /sorprendeme)
      let donationIdFromDb = `donation_${Date.now()}`;
      const amount = method === "stripe" ? TEST_AMOUNT_USD : TEST_AMOUNT_ARS;
      const currency = method === "stripe" ? "USD" : "ARS";

      try {
        const saveResponse = await fetch("/api/donations/save-message", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount,
            currency,
            message: "Prueba de pago - Suscriptor exclusivo",
            donor_name: "Suscriptor",
            donor_email: null,
            is_test: true, // Permite montos menores para pruebas
          }),
        });

        if (saveResponse.ok) {
          const saveData = await saveResponse.json() as { donation_id: string };
          donationIdFromDb = saveData.donation_id;
        }
      } catch (dbError) {
        console.warn('[TestExclusivo] DB save failed, continuing with payment:', dbError);
      }

      setDonationId(donationIdFromDb);

      // 2. Crear payment intent/preference
      if (method === "stripe") {
        const response = await fetch("/api/create-payment-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: TEST_AMOUNT_USD * 100, // cents
            currency: "usd",
            productName: "Regalo - Test Exclusivo ✨",
            metadata: {
              source: BRAND_SOURCE_KEY,
              type: "gift",
              product_type: "gift",
              donation_id: donationIdFromDb,
              page: "test-exclusivo",
            },
          }),
        });

        if (!response.ok) throw new Error("Error al crear pago");

        const data = await response.json() as { clientSecret: string };
        setClientSecret(data.clientSecret);
        setStep("checkout");
      } else {
        const response = await fetch("/api/mercadopago/create-preference", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            productName: "Regalo - Test Exclusivo ✨",
            price: TEST_AMOUNT_ARS,
            currency: "ARS",
            country: "AR",
            successPath: "/test-exclusivo?status=success",
            failurePath: "/test-exclusivo?status=error",
            pendingPath: "/test-exclusivo?status=pending",
            metadata: {
              source: BRAND_SOURCE_KEY,
              type: "gift",
              product_type: "gift",
              donation_id: donationIdFromDb,
              page: "test-exclusivo",
            },
          }),
        });

        if (!response.ok) throw new Error("Error al crear pago");

        const data = await response.json() as { preferenceId: string };
        setPreferenceId(data.preferenceId);
        setStep("checkout");
      }
    } catch (error) {
      console.error("Error:", error);
      setStep("error");
      setTimeout(() => setStep("select"), 3000);
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
    setStep("select");
    setPaymentMethod(null);
    setClientSecret(null);
    setPreferenceId(null);
  };

  const handleSuccess = () => {
    setStep("success");
  };

  // Pending screen
  if (step === "pending") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-900 via-orange-900 to-amber-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-black/60 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center space-y-6 border border-yellow-500/30"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="text-8xl"
          >
            ⏳
          </motion.div>
          <h1 className="text-3xl font-bold text-white">Procesando...</h1>
          <p className="text-white/70">
            Tu pago está siendo procesado. ¡En un momento estará listo!
          </p>
        </motion.div>
      </div>
    );
  }

  // Success screen
  if (step === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-black/60 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center space-y-6 border border-green-500/30"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="text-8xl"
          >
            💖
          </motion.div>
          <h1 className="text-3xl font-bold text-white">¡Recibí tu regalo!</h1>
          <p className="text-white/70">
            La transacción se procesó correctamente. ¡Gracias por tu apoyo! 💕
          </p>
          <button
            onClick={() => {
              setStep("select");
              setDonationId(null);
            }}
            className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-xl hover:opacity-90 transition-opacity"
          >
            🎁 Enviar otro regalo
          </button>
          <p className="text-white/40 text-sm">
            Con amor, {BRAND_NAME} 💕
          </p>
        </motion.div>
      </div>
    );
  }

  // Error screen
  if (step === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-rose-900 to-pink-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-black/60 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center space-y-6 border border-red-500/30"
        >
          <div className="text-8xl">😔</div>
          <h1 className="text-3xl font-bold text-white">Ups, algo falló</h1>
          <p className="text-white/70">
            No pude procesar tu pago. Redirigiendo...
          </p>
        </motion.div>
      </div>
    );
  }

  // Checkout screen - Stripe
  if (step === "checkout" && paymentMethod === "stripe" && clientSecret) {
    const stripePromise = getStripe();
    if (!stripePromise) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <p className="text-white">Stripe no configurado</p>
        </div>
      );
    }

    return (
      <div 
        className="h-dvh flex flex-col bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900"
        style={{
          // Safari iOS & Android: Prevent layout issues
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: STRIPE_APPEARANCE,
          }}
        >
          <div className="flex-1 flex flex-col min-h-0">
            <StripeCheckoutForm
              amount={TEST_AMOUNT_USD * 100}
              currency="usd"
              packName="Regalo - Test Exclusivo ✨"
              language="es"
              onSuccess={handleSuccess}
              onError={(err) => {
                console.error("[TestExclusivo] Payment error:", err);
                setStep("error");
              }}
              onBack={handleBack}
              flowSurface="test_exclusivo_stripe"
            />
          </div>
        </Elements>
      </div>
    );
  }

  // Checkout screen - MercadoPago
  if (step === "checkout" && paymentMethod === "mercadopago" && preferenceId) {
    return (
      <div className="h-screen w-screen overflow-hidden">
        <MercadoPagoCheckoutForm
          amount={TEST_AMOUNT_ARS}
          preferenceId={preferenceId}
          packName="Regalo - Test Exclusivo ✨"
          paymentType="one-time"
          metadata={{
            source: BRAND_SOURCE_KEY,
            type: "gift",
            product_type: "gift",
            donation_id: donationId,
            page: "test-exclusivo",
          }}
          externalReference={`donation_${donationId || Date.now()}`}
          onSuccess={handleSuccess}
          onBack={handleBack}
        />
      </div>
    );
  }

  // Selection screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-black/60 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full space-y-8 border border-white/20"
      >
        {/* Header */}
        <div className="text-center space-y-4">
          <motion.img
            src={HEADER_PROFILE_IMAGE_URL}
            alt={BRAND_PERSON_NAME}
            className="w-20 h-20 rounded-full mx-auto border-4 border-white/20"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.1 }}
          />
          <div>
            <h1 className="text-2xl font-bold text-white">
              Zona Exclusiva 🔒
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Solo para suscriptores
            </p>
          </div>
        </div>

        {/* Payment options */}
        <div className="space-y-4">
          <p className="text-center text-white/80 text-sm">
            Elige tu método de pago:
          </p>

          {/* Stripe button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleSelectPayment("stripe")}
            disabled={processing}
            className="w-full py-5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold rounded-2xl flex items-center justify-center gap-3 hover:opacity-90 ui-transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/30"
          >
            {processing && paymentMethod === "stripe" ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <span className="text-2xl">💳</span>
                <div className="text-left">
                  <div className="text-base">Pagar con Stripe</div>
                  <div className="text-xs opacity-80">${TEST_AMOUNT_USD} USD → 0.1 puntos*</div>
                </div>
              </>
            )}
          </motion.button>

          {/* MercadoPago button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleSelectPayment("mercadopago")}
            disabled={processing}
            className="w-full py-5 bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold rounded-2xl flex items-center justify-center gap-3 hover:opacity-90 ui-transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/30"
          >
            {processing && paymentMethod === "mercadopago" ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <span className="text-2xl">🏦</span>
                <div className="text-left">
                  <div className="text-base">Pagar con MercadoPago</div>
                  <div className="text-xs opacity-80">${TEST_AMOUNT_ARS.toLocaleString()} ARS → 1 punto</div>
                </div>
              </>
            )}
          </motion.button>

          {/* Points info */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-center space-y-1">
            <p className="text-white/60 text-xs">
              <span className="text-yellow-400">⭐</span> Sistema de puntos:
            </p>
            <p className="text-white/50 text-xs">
              1 punto = $10 USD = $1,400 ARS
            </p>
            <p className="text-white/40 text-xs italic">
              *Con $1 USD recibirás 0 puntos (mínimo $10 para 1 punto)
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 text-green-400 text-sm">
            <span>🔒</span>
            <span>Pago seguro</span>
          </div>
          <p className="text-white/40 text-xs">
            Link exclusivo para suscriptores 💕
          </p>
        </div>
      </motion.div>
    </div>
  );
}
